import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  getDepartment,
  getDepartments,
  getDoctors,
} from "@/lib/server-functions";
import DepartmentDetail from "@/pages/DepartmentDetail";
import ogImage from "@/assets/knmc-og.jpeg";
import { getPageUrl } from "@/lib/site";
import { departmentJsonLd } from "@/lib/jsonld";

export const Route = createFileRoute("/departments/$name")({
  loader: async ({ params }) => {
    try {
      const [department, departments, doctors] = await Promise.all([
        getDepartment({ data: params.name }),
        getDepartments(),
        getDoctors(),
      ]);
      return { department, departments, doctors };
    } catch {
      throw notFound();
    }
  },

  head: ({ params, loaderData }) => {
    const pagePath = `/departments/${encodeURIComponent(params.name)}`;
    const pageUrl = getPageUrl(pagePath);

    return {
      meta: [
        {
          title: `${params.name} — KNMC Hospital, Kathmandu`,
        },
        {
          name: "description",
          content: `${params.name} care at KNMC, a leading hospital in Kathmandu, Nepal. Expert specialists, modern facilities and online appointment booking.`,
        },
        {
          name: "keywords",
          content: `${params.name} Nepal, ${params.name} hospital Kathmandu, ${params.name} department KNMC, KNMC hospital departments`,
        },
        {
          property: "og:title",
          content: `${params.name} in Kathmandu — KNMC Hospital, Nepal`,
        },
        {
          property: "og:description",
          content: `Expert ${params.name} care at KNMC Hospital in Kathmandu, Nepal. Book appointments online.`,
        },
        {
          property: "og:image",
          content: getPageUrl(ogImage),
        },
        {
          property: "og:url",
          content: pageUrl,
        },
        ...(loaderData?.department
          ? [departmentJsonLd(loaderData.department, loaderData.doctors, pagePath)]
          : []),
      ],

      links: [
        {
          rel: "canonical",
          href: pageUrl,
        },
      ],
    };
  },

  component: () => {
    const { department, departments, doctors } = Route.useLoaderData();

    return (
      <DepartmentDetail
        department={department}
        departments={departments}
        doctors={doctors}
      />
    );
  },
});